import React, { FC, useState } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { ActivityItem } from 'components/molecules/Generic/ActivityItem';
import InboxOutlined from 'assets/svg/InboxOutlined';
import Bookmark from 'assets/svg/Bookmark';
import { formatActivityDate, formatActivityTime } from 'utils/ppaHelpers';


const ACTIVITY_FEED_TAB_KEYS = {
    LAST_VISITED: 'lastVisited',
    BOOKMARK: 'bookmark',
} as const;

type ActivityFeedTabKey = typeof ACTIVITY_FEED_TAB_KEYS[keyof typeof ACTIVITY_FEED_TAB_KEYS];

interface ActivityFeedWidgetProps {
    tabs?: Array<{ key: ActivityFeedTabKey; label: string; icon?: React.ReactNode }>;
    defaultActiveTab?: ActivityFeedTabKey;
    lastVisitedItems?: any;
    bookmarkItems?: any;
    style?: React.CSSProperties;
    className?: string;
}

const defaultTabs: ActivityFeedWidgetProps['tabs'] = [
    { key: ACTIVITY_FEED_TAB_KEYS.LAST_VISITED, label: 'Last Visited' },
    { key: ACTIVITY_FEED_TAB_KEYS.BOOKMARK, label: 'Bookmark' },
];

interface ActivityFeedEmptyStateProps {
    title: string;
    description: string;
}

const ActivityFeedEmptyState: FC<ActivityFeedEmptyStateProps> = ({ title, description }) => (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-8 py-6">
        <div className="flex h-[61px] w-[96px] shrink-0 items-center justify-center">
            <InboxOutlined customIndex={0} />
        </div>
        <div className="flex flex-col items-center gap-1 text-center">
            <p className="m-0 text-base font-semibold leading-8 text-black/88">{title}</p>
            <p className="m-0 text-base font-normal leading-[22px] text-black/45">{description}</p>
        </div>
    </div>
);

interface LastVisitedTabContentProps {
    isActive: boolean;
    items: any;
}

const LastVisitedTabContent: FC<LastVisitedTabContentProps> = ({ isActive, items }) => (
    <div
        className={isActive ? 'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto' : 'hidden'}
        style={items.length > 3 ? { maxHeight: 'calc(3 * 54px + 2 * 12px)' } : undefined}
    >
        {items.length > 0 ? (
            items.map((item: any, i: number) => {

                return (
                    <ActivityItem
                        key={item.item_id ?? `last-${i}`}
                        title={item.item_name}
                        date={formatActivityDate(item.visited_at)}
                        time={formatActivityTime(item.visited_at)}
                        onClick={item.onClick}
                    />
                )
            }
            )
        ) : (
            <ActivityFeedEmptyState
                title="No Recent Activity"
                description="Pages you visit will appear here."
            />
        )}
    </div>
);

interface BookmarkTabContentProps {
    isActive: boolean;
    items: any;
}

const BookmarkTabContent: FC<BookmarkTabContentProps> = ({ isActive, items }) => (
    <div className={isActive ? 'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto' : 'hidden'}>
        {items.length > 0 ? (
            items.map((item: any, i: number) => (
                <ActivityItem
                    key={item.item_id ?? `bookmark-${i}`}
                    title={item.item_name}
                    date={formatActivityDate(item.visited_at)}
                    time={formatActivityTime(item.visited_at)}
                    onClick={item.onClick}
                />
            ))
        ) : (
            <ActivityFeedEmptyState
                title="Coming Soon!"
                description="Get your saved bookmarks here."
            />
        )}
    </div>
);

interface ActivityFeedContentProps {
    activeTab: ActivityFeedTabKey;
    lastVisitedItems: any;
    bookmarkItems: any;
}

const ActivityFeedContent: FC<ActivityFeedContentProps> = ({
    activeTab,
    lastVisitedItems,
    bookmarkItems,
}) => (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        <LastVisitedTabContent isActive={activeTab === ACTIVITY_FEED_TAB_KEYS.LAST_VISITED} items={lastVisitedItems} />
        <BookmarkTabContent isActive={activeTab === ACTIVITY_FEED_TAB_KEYS.BOOKMARK} items={bookmarkItems} />
    </div>
);

export const ActivityFeedWidget: FC<ActivityFeedWidgetProps> = ({
    tabs = defaultTabs,
    defaultActiveTab = ACTIVITY_FEED_TAB_KEYS.LAST_VISITED,
    lastVisitedItems = [],
    bookmarkItems = [],
    style,
    className = '',
}) => {
    const [activeTab, setActiveTab] = useState<ActivityFeedTabKey>(defaultActiveTab);

    return (
        <div
            className={`flex min-h-[280px] min-w-0 flex-1 flex-col gap-3 rounded-xl bg-backgroundGreyAlt p-4 ${className}`}
            style={style}
        >
            <div className="flex w-fit shrink-0 gap-2 rounded-lg bg-[#f4f4f5] p-1">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex w-[145px] shrink-0 items-center justify-center gap-2 rounded-lg px-[39px] py-1 text-sm font-medium leading-[22px] ${isActive
                                ? 'border border-purpleFont bg-white text-purpleFont'
                                : 'border-transparent bg-transparent text-[#63748a]'
                                }`}
                        >
                            {tab.icon ??
                                (tab.key === ACTIVITY_FEED_TAB_KEYS.LAST_VISITED ? (
                                    <ClockCircleOutlined
                                        className={`shrink-0 text-base ${isActive ? 'text-purpleFont' : 'text-[#63748a]'}`}
                                    />
                                ) : tab.key === ACTIVITY_FEED_TAB_KEYS.BOOKMARK ? (
                                    <Bookmark
                                        className="shrink-0"
                                        size={18}
                                        color={isActive ? '#44338a' : '#63748a'}
                                    />
                                ) : null)}
                            <span className="whitespace-nowrap">{tab.label}</span>
                        </button>
                    );
                })}
            </div>
            <ActivityFeedContent
                activeTab={activeTab}
                lastVisitedItems={lastVisitedItems}
                bookmarkItems={bookmarkItems}
            />
        </div>
    );
};

export default ActivityFeedWidget;
